import { useState } from 'react'
import Navigation from '@/components/Navigation'
import { Link } from 'wouter'
import { usePageMeta } from '@/hooks/usePageMeta'
import { trackEvent } from '@/lib/analytics'


const DonatePage = () => {
  usePageMeta({
    title: 'Support SPOKE | Help Us Reach Millions',
    description: 'Support the release of SPOKE. Every dollar helps bring this firefighter tribute documentary to theaters and start the conversation that saves lives.',
  })
  const [selectedAmount, setSelectedAmount] = useState<string | null>(null)
  const [selectedStripeUrl, setSelectedStripeUrl] = useState<string | null>(null)

  const STRIPE_DEFAULT = 'https://donate.stripe.com/28E14m69tdbWcPL8qYgUM00'

  const [showContactModal, setShowContactModal] = useState(false)

  const quickAmounts = [
    { amount: '$25',     label: 'Supporter',         stripeUrl: 'https://buy.stripe.com/bJeaEWbtNgo83fbaz6gUM01', contactOnly: false },
    { amount: '$100',    label: 'Supporter',         stripeUrl: 'https://buy.stripe.com/00w5kCgO72xidTPbDagUM02', contactOnly: false },
    { amount: '$250',    label: 'Supporter',         stripeUrl: 'https://buy.stripe.com/aFa14m8hB0pa9Dz8qYgUM03', contactOnly: false },
    { amount: '$1,000',  label: 'Friend of SPOKE',   stripeUrl: 'https://buy.stripe.com/14AaEW7dx4Fq2b78qYgUM04', contactOnly: false },
    { amount: '$5,000',  label: 'Advocate Circle',   stripeUrl: 'https://buy.stripe.com/bJe14m9lF6Ny4jfaz6gUM05', contactOnly: false },
    { amount: '$10,000', label: 'Community Partner', stripeUrl: '',                                               contactOnly: true  },
    { amount: '$25,000', label: 'Outreach Champion', stripeUrl: '',                                               contactOnly: true  },
    { amount: '$50,000', label: 'Impact Producer',   stripeUrl: '',                                               contactOnly: true  },
  ]

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white">
      <Navigation />

      {/* ── HERO ── */}
      <div className="pt-40 pb-20 px-6 bg-[#0a0f1a]">
        <div className="max-w-5xl mx-auto">
          <div className="w-12 h-1 bg-yellow-400 mx-auto mb-10"></div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-black uppercase tracking-wide text-white mb-4 text-center leading-tight">
            How Do You Want to Give?
          </h1>
          <p className="text-base md:text-lg font-poppins font-light text-gray-400 text-center mb-12">
            Two paths. Same impact. You pick what works for you.
          </p>

          {/* Two giving path cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Donate Directly */}
            <div className="border border-gray-700 bg-[#111827] p-8 flex flex-col" style={{ borderTopColor: '#facc15', borderTopWidth: '3px' }}>
              <p className="text-yellow-400 font-montserrat font-black uppercase tracking-widest text-xs mb-3">Maximum Impact</p>
              <h2 className="text-2xl md:text-3xl font-montserrat font-black uppercase text-white mb-4">Donate Directly</h2>
              <p className="font-poppins text-gray-300 text-base leading-relaxed mb-8 flex-1">
                Every dollar goes straight to the film. No platform fees, no cut. This is the fastest way to move the needle.
              </p>
              <a
                href="#donate"
                onClick={() => trackEvent('donation_click', {
                  site: 'spoke',
                  film: 'spoke',
                  action: 'select-amount',
                  placement: 'giving-path',
                  provider: 'internal',
                })}
                className="block w-full bg-yellow-400 hover:bg-yellow-300 text-black font-montserrat font-black uppercase tracking-widest text-base px-6 py-4 text-center transition-colors duration-300"
              >
                Donate Directly
              </a>
            </div>

            {/* Donate via IDA */}
            <div className="border border-gray-700 bg-[#111827] p-8 flex flex-col">
              <p className="text-gray-400 font-montserrat font-black uppercase tracking-widest text-xs mb-3">Tax-Deductible</p>
              <h2 className="text-2xl md:text-3xl font-montserrat font-black uppercase text-white mb-4">Donate via IDA</h2>
              <p className="font-poppins text-gray-300 text-base leading-relaxed mb-8 flex-1">
                Need a tax receipt? SPOKE is fiscally sponsored by the Independent Documentary Association, a 501(c)(3). Donations through IDA are tax-deductible.
              </p>
              <a
                href="https://www.documentary.org/project/spoke"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('donation_click', {
                  site: 'spoke',
                  film: 'spoke',
                  action: 'open',
                  placement: 'giving-path',
                  provider: 'ida',
                })}
                className="block w-full border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-montserrat font-black uppercase tracking-widest text-base px-6 py-4 text-center transition-colors duration-300"
              >
                Donate via IDA
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── WHERE YOUR MONEY GOES ── */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="w-8 h-[2px] bg-red-600 mb-6 md:mx-auto"></div>
          <h2 className="font-montserrat font-black uppercase tracking-wide text-white text-2xl md:text-3xl mb-2 md:text-center">
            Where Your Money Goes
          </h2>
          <p className="font-poppins text-gray-400 text-sm mb-10 md:text-center">
            Every dollar goes directly toward getting SPOKE seen.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
            {[
              { icon: '🎬', label: 'Festival submissions & attendance' },
              { icon: '📢', label: 'Marketing, advertising & social media campaign' },
              { icon: '🎞️', label: 'National one-week theatrical release' },
              { icon: '🎙️', label: 'Official SPOKE podcast production' },
              { icon: '🗓️', label: 'Community & benefit screening events' },
              { icon: '📰', label: 'PR team & press outreach' },
              { icon: '🤝', label: 'Outreach partnerships & organizations' },
              { icon: '✈️', label: 'Filmmaker travel & Q&A appearances' },
              { icon: '⚙️', label: 'Distribution & delivery costs' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-start gap-3 py-2 border-b border-gray-800">
                <span className="text-base mt-0.5 w-6 flex-shrink-0">{icon}</span>
                <span className="font-poppins text-gray-300 text-sm leading-snug">{label}</span>
              </div>
            ))}
          </div>
          <p className="font-poppins text-gray-500 text-sm mt-8 italic">
            Donors will receive regular updates on how funds are spent.
          </p>
        </div>
      </section>

      {/* ── DONATE CTA ── */}
      <section id="donate" className="py-20 bg-[#0a0f1a]">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <div className="w-12 h-1 bg-yellow-400 mx-auto mb-8"></div>
          <h2 className="text-3xl md:text-4xl font-montserrat font-black uppercase tracking-wide text-white mb-4">
            Choose Your Amount
          </h2>
          <p className="font-poppins text-gray-400 mb-10 text-base leading-relaxed">
            Every gift helps expand the release and the movement around the film.
          </p>

          {/* Quick amounts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {quickAmounts.map(({ amount, label, stripeUrl, contactOnly }) => (
              <button
                key={amount}
                onClick={() => {
                  trackEvent('donation_click', {
                    site: 'spoke',
                    film: 'spoke',
                    placement: 'amount_selector',
                    provider: contactOnly ? 'contact' : 'stripe',
                    amount_tier: amount.replace('$', '').replace(',', ''),
                  })
                  if (contactOnly) {
                    setShowContactModal(true)
                  } else {
                    setSelectedAmount(amount)
                    setSelectedStripeUrl(stripeUrl)
                  }
                }}
                className={`py-4 px-2 flex flex-col items-center justify-center border transition-colors duration-200 ${
                  contactOnly
                    ? 'border-gray-700 text-gray-500 hover:border-red-600 hover:text-red-500 cursor-pointer'
                    : selectedAmount === amount
                      ? 'bg-yellow-400 text-black border-yellow-400'
                      : 'border-gray-600 text-gray-300 hover:border-yellow-400 hover:text-yellow-400'
                }`}
              >
                <span className="font-montserrat font-black text-lg leading-tight">{amount}</span>
                <span className={`text-xs mt-1 font-poppins tracking-wide ${
                  contactOnly ? 'text-gray-600' : selectedAmount === amount ? 'text-black/70' : 'text-gray-500'
                }`}>{contactOnly ? 'Contact Team' : label}</span>
              </button>
            ))}
          </div>

          {/* Custom amount — links to Stripe where donor types their own amount */}
          <div className="mb-8">
            <a
              href={STRIPE_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent('donation_click', {
                  site: 'spoke',
                  film: 'spoke',
                  action: 'open',
                  placement: 'custom-amount',
                  provider: 'stripe',
                })
                setSelectedAmount(null)
                setSelectedStripeUrl(null)
              }}
              className={`flex flex-col items-center justify-center border transition-colors duration-200 py-4 px-2 w-full ${
                selectedAmount === null
                  ? 'border-gray-600 text-gray-300 hover:border-yellow-400 hover:text-yellow-400'
                  : 'border-gray-600 text-gray-300 hover:border-yellow-400 hover:text-yellow-400'
              }`}
            >
              <span className="font-montserrat font-black text-lg leading-tight">Choose</span>
              <span className="text-xs mt-1 font-poppins tracking-wide text-gray-500">Your amount</span>
            </a>
          </div>

          <a
            href={selectedStripeUrl ?? STRIPE_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block w-full bg-yellow-400 hover:bg-yellow-300 text-black font-montserrat font-black uppercase tracking-widest text-lg py-5 transition-colors duration-300 mb-4"
            onClick={() => trackEvent('donation_click', {
              site: 'spoke',
              film: 'spoke',
              placement: 'donate_now',
              provider: 'stripe',
              amount_tier: selectedAmount ? selectedAmount.replace('$', '').replace(',', '') : 'custom',
            })}
          >
            {selectedAmount ? `Donate ${selectedAmount}` : 'Donate Now'}
          </a>

          {/* Trust strip */}
          <ul className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 text-gray-600 text-xs font-poppins mt-2">
            <li className="flex items-center gap-1.5"><span className="text-yellow-400">✓</span> Secure mobile-friendly checkout</li>
            <li className="flex items-center gap-1.5"><span className="text-yellow-400">✓</span> Quick donation with minimal friction</li>
            <li className="flex items-center gap-1.5"><span className="text-yellow-400">✓</span> Funds theatrical release &amp; outreach</li>
          </ul>
        </div>
      </section>

      {/* ── WHY NOW (recall) ── */}
      <section className="py-16 bg-[#0a0f1a]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="w-8 h-[2px] bg-red-600 mb-6"></div>
          <h2 className="font-montserrat font-black uppercase tracking-wide text-white text-2xl md:text-3xl mb-6">
            Why Now?
          </h2>
          <p className="font-poppins text-gray-400 text-base md:text-lg leading-relaxed">
            Most independent films never reach the audiences they were made for because release and publicity costs can rival or exceed production budgets. We made SPOKE for $500,000; now we need the campaign that can carry it into theaters, headlines, classrooms, communities, and cultural conversation.
          </p>
        </div>
      </section>

      {/* ── MAJOR GIFT ── */}
      <section id="major-gift" className="py-20 bg-[#0a0f1a]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="w-12 h-1 bg-yellow-400 mx-auto mb-8"></div>
          <h2 className="text-3xl md:text-4xl font-montserrat font-black uppercase tracking-wide text-white mb-6">
            Bring SPOKE to the Next Level
          </h2>
          <p className="font-poppins text-gray-400 text-lg leading-relaxed mb-10">
            For gifts of $10,000 or more, we'd love to speak with you directly about impact, recognition, screenings, and partnership opportunities.
          </p>
          <Link
            href="/get-in-touch"
            onClick={() => trackEvent('contact_click', { site: 'spoke', film: 'spoke', placement: 'major_gift', provider: 'internal' })}
            className="inline-block border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-montserrat font-black uppercase tracking-widest text-base px-10 py-4 transition-colors duration-300"
          >
            Talk with the Team
          </Link>
        </div>
      </section>

      {/* ── CONTACT MODAL ── */}
      {showContactModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-6"
          onClick={() => setShowContactModal(false)}
        >
          <div
            className="bg-[#0a0f1a] border border-gray-700 max-w-md w-full p-10 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-8 h-[2px] bg-yellow-400 mx-auto mb-6"></div>
            <h3 className="font-montserrat font-black uppercase tracking-wide text-white text-xl mb-4">
              Please Contact the Team
            </h3>
            <p className="font-poppins text-gray-400 text-sm leading-relaxed mb-8">
              For gifts of $10,000 or more, we'd love to connect with you directly about impact, recognition, and partnership opportunities.
            </p>
            <Link
              href="/get-in-touch"
              onClick={() => trackEvent('contact_click', { site: 'spoke', film: 'spoke', placement: 'donation_modal', provider: 'internal' })}
              className="inline-block bg-yellow-400 hover:bg-yellow-300 text-black font-montserrat font-black uppercase tracking-widest text-sm px-8 py-3 transition-colors duration-300 mb-4"
            >
              Contact Us
            </Link>
            <br />
            <button
              onClick={() => setShowContactModal(false)}
              className="mt-2 text-xs font-poppins text-gray-600 hover:text-gray-400 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <div className="text-center py-12 border-t border-gray-800">
        <div className="flex flex-wrap justify-center gap-6 mb-6">
          <Link href="/" className="font-poppins text-sm text-gray-500 hover:text-white transition-colors">Home</Link>
          <Link href="/get-in-touch" className="font-poppins text-sm text-gray-500 hover:text-white transition-colors">Contact Us</Link>
          <a href="https://filmabee.com" className="font-poppins text-sm text-yellow-400 hover:text-yellow-300 transition-colors">Filmabee Studios</a>
        </div>
        <p className="font-poppins text-xs text-gray-700">© 2025 Filmabee Studios. All rights reserved.</p>
      </div>
    </div>
  )
}

export default DonatePage
